"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/useAuthStore";
import { toastApiError } from "@/lib/api-toast";
import { getComments, createComment } from "../api";
import type { Comment, CommentableModel, CreateCommentPayload } from "../types";

/** Fetch + post for one model's comments, shared by the legacy and v2
 *  comment sections so both post exactly the same payload. */
export function useCommentThread({
  modelId,
  modelType,
  isFa
}: {
  modelId: number;
  modelType: CommentableModel;
  isFa: boolean;
}) {
  const {
    user
  } = useAuthStore();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One post at a time: a double press (or Enter with a press) never posts the comment twice.
  const inFlight = useRef(false);
  const [commentText, setCommentText] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getComments({
        id: modelId,
        type: modelType,
        set_child: true,
        set_parent: true
      });
      // Show the latest 3 comments.
      const items = res.data?.items || [];
      setComments(items.slice(-3));
    } catch (error) {
      console.error("Failed to fetch comments", error);
    } finally {
      setIsLoading(false);
    }
  }, [modelId, modelType]);
  useEffect(() => {
    void fetchComments();
  }, [fetchComments]);
  const submit = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (!commentText.trim() || inFlight.current) return;
    try {
      inFlight.current = true;
      setIsSubmitting(true);
      const payload: CreateCommentPayload = {
        content: commentText,
        name: user.name || user.username || "User",
        email: "user@example.com",
        // Placeholder as email is not in user store interface
        model_id: modelId,
        model_type: modelType,
        review_rate: rating
      };
      await createComment(payload);
      toast.success(isFa ? "دیدگاه شما ثبت شد" : "Comment posted successfully");
      setCommentText("");
      setRating(null);
      void fetchComments();
    } catch (error_0) {
      console.error("Post comment error", error_0);
      toastApiError(error_0, isFa ? "خطا در ثبت دیدگاه" : "Failed to post comment");
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };
  return {
    user,
    comments,
    isLoading,
    isSubmitting,
    commentText,
    setCommentText,
    rating,
    setRating,
    submit,
    showLoginModal,
    setShowLoginModal
  };
}